import { useCallback } from 'react';

import type { BundleItem } from './bundle-item-row';
import { BundleContentsEditor } from './bundle-preview-editor';
import { buildDefaultBundleDraft } from './bundle-preview-utils';
import { ConfigTab, type BundleConfig } from './config-tab';
import useExportBundle from '@/api/mutations/useExportBundle';
import type { DownloadFormValues } from '@/new/components/download/types';
import type { BundleVariantConfig } from '@/new/components/download/types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';

interface BundleContentProps {
  variantId: string;
  config: BundleVariantConfig;
  onClose: () => void;
  onBack?: () => void;
  onAsyncStarted?: (payload: {
    variantId: string;
    exportId: string;
    jobId?: string;
    format: DownloadFormValues['format'];
    bundleName?: string;
  }) => void;
  draftState?: BundleDraftState;
  onDraftStateChange?: (draft: BundleDraftState) => void;
}

export interface BundleDraftState {
  items: BundleItem[];
  formConfig: BundleConfig;
}

export const BundleContent = ({
  variantId,
  config: bundleConfig,
  onClose,
  onBack,
  onAsyncStarted,
  draftState,
  onDraftStateChange,
}: BundleContentProps) => {
  const defaultDraft = buildDefaultBundleDraft(bundleConfig);
  const draft = draftState ?? defaultDraft;
  const items = draft.items;
  const formConfig = {
    ...defaultDraft.formConfig,
    ...draft.formConfig,
  };

  const { exportMutation } = useExportBundle();

  const updateDraft = useCallback(
    (nextDraft: BundleDraftState) => {
      onDraftStateChange?.(nextDraft);
    },
    [onDraftStateChange],
  );

  const handleItemsChange = useCallback(
    (nextItems: BundleItem[]) => {
      updateDraft({ items: nextItems, formConfig });
    },
    [formConfig, updateDraft],
  );

  const handleConfigChange = useCallback(
    (nextConfig: BundleConfig) => {
      updateDraft({ items, formConfig: nextConfig });
    },
    [items, updateDraft],
  );

  const handleExport = async () => {
    if (items.length === 0 || !formConfig.name.trim()) return;

    const { name, ...config } = formConfig;

    try {
      const exportResult = await exportMutation.mutateAsync({
        matterId: bundleConfig.matterId,
        subsetId: bundleConfig.subsetId,
        name,
        config,
        items: items.map(({ schemaName, schemaId, customName, dateText }) => ({
          schemaName,
          schemaId,
          customName: customName ?? undefined,
          dateText: dateText ?? undefined,
        })),
      });

      onClose();
      onAsyncStarted?.({
        variantId,
        exportId: exportResult.exportId,
        jobId: exportResult.jobId,
        format: 'pdf' as DownloadFormValues['format'],
        bundleName: name,
      });
    } catch {
      // Mutation hook surfaces the toast.
    }
  };

  const isExporting = exportMutation.isLoading;

  return (
    <div className="flex min-h-0 flex-col">
      <div className="flex min-h-0 flex-row gap-4 overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col">
          <BundleContentsEditor
            items={items}
            config={formConfig}
            onItemsChange={handleItemsChange}
            onConfigChange={handleConfigChange}
          />
        </div>

        <div className="w-full max-w-sm shrink-0 overflow-y-auto p-2">
          <ConfigTab config={formConfig} onConfigChange={handleConfigChange} />
        </div>
      </div>

      <div className="mt-5 flex shrink-0 justify-between gap-2">
        <div>
          {onBack && (
            <Button variant="ghost" onClick={onBack}>
              <Icon className="lucide--chevron-left" />
              Back
            </Button>
          )}
        </div>

        <div className="flex gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={items.length === 0 || !formConfig.name.trim() || isExporting}
          >
            {isExporting ? 'Creating...' : 'Download as PDF'}
          </Button>
        </div>
      </div>
    </div>
  );
};
